import { FaTrash } from "react-icons/fa";
import type React from "react";

import { Button, Checkbox, IconButton } from "../../../ui";
import { RecurringAlertIcon, SoundAlertIcon } from "../../../icons";
import { Tooltip, TooltipTrigger } from "@artis/ui";

import { InputOrReference, ManageTable } from "../Shared";
import type { TAlert, TAlertOptions } from "../../../../data/alerts/queries";
import { playAlertSound } from "../../../alerts/sound";
import {
  isAlertFullyDisabled,
  isAlertLimitPeriodExpired,
  isAlertPeriodExpired,
  isExpiredAt,
  resolveExpiredAt,
} from "../../../../data/alerts/expiration";
import { rowIdToPeriod } from "../../../../data/formatting/helpers";
import { periodFromCellRef } from "../../../../data/formatting/cell-ref";
import { ExpirationPicker } from "./ExpirationPicker";

const StatusBadge = ({
  status,
  isExpired,
  isPeriodExpired,
  disabled,
  onClick,
}: {
  status: "Active" | "Paused";
  isExpired: boolean;
  isPeriodExpired: boolean;
  disabled: boolean;
  onClick: () => void;
}) => {
  const isActive = status === "Active" && !isExpired && !isPeriodExpired;
  const displayStatus = isExpired ? "Expired" : isActive ? "Active" : "Inactive";

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="flex justify-center items-center text-xs font-medium"
      style={{
        padding: "var(--spacing-scale-small-px, 1px) var(--spacing-scale-small-200, 6px)",
        gap: "var(--spacing-scale-small-150, 4.5px)",
        borderRadius: "var(--border-radius-rounded-full, 9999px)",
        backgroundColor: isExpired
          ? "var(--semantic-danger-fill)"
          : isActive
            ? "var(--semantic-success-fill)"
            : "var(--semantic-neutral-fill)",
        color: isExpired
          ? "var(--semantic-on-danger)"
          : isActive
            ? "var(--semantic-on-success)"
            : "var(--semantic-on-neutral)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.8 : 1,
      }}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{
          backgroundColor: isExpired
            ? "var(--semantic-danger)"
            : isActive
              ? "var(--semantic-success)"
              : "var(--semantic-neutral)",
        }}
      />
      {displayStatus}
    </button>
  );
};

export const AlertRow = ({
  value,
  pageId,
  affected,
  affectedTooltip,
  selected,
  onSelect,
  onUpdate,
  onRemove,
  noteWidth = 120,
  limitWidth = 150,
  /**
   * The period/contract-derived expiration that the "None" option resolves
   * to. Known up-front in Manage (row_id is fixed); null in Add, where a
   * draft row can still apply to several cells/periods, so it's resolved
   * per-cell at save time instead.
   */
  autoExpiredAt = null,
}: {
  value: TAlert | (TAlertOptions & { changed?: boolean });
  pageId: string;
  affected?: React.ReactNode;
  affectedTooltip?: React.ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  onUpdate: (rule: Partial<TAlert>) => void;
  onRemove?: () => void;
  noteWidth?: number;
  limitWidth?: number;
  autoExpiredAt?: string | null;
}) => {
  const now = new Date();
  const isPersisted = "row_id" in value;
  const snapshot = {
    row_id: isPersisted ? value.row_id : undefined,
    limit: value.limit,
    expired_at: "expired_at" in value ? (value.expired_at as string | null | undefined) : undefined,
  };

  // Date expiry → "Expired". Contract (alert period or limit ref) → "Inactive".
  const isExpired = isExpiredAt(resolveExpiredAt(snapshot), now);
  const isAlertPeriodExpiredFlag = isPersisted ? isAlertPeriodExpired({ row_id: value.row_id }, now) : false;
  const isLimitPeriodExpired = isAlertLimitPeriodExpired({ limit: value.limit }, now);

  const alertPeriod = isPersisted && value.row_id ? rowIdToPeriod(value.row_id) : undefined;
  const limitRefPeriod = periodFromCellRef(value.limit);
  const expirationReason = isExpired
    ? "Alert has expired"
    : isAlertPeriodExpiredFlag && isLimitPeriodExpired
      ? `Alert period (${alertPeriod}) and limit reference (${limitRefPeriod}) have expired`
      : isAlertPeriodExpiredFlag
        ? `Alert period (${alertPeriod}) has expired`
        : isLimitPeriodExpired
          ? `Limit reference period (${limitRefPeriod}) has expired`
          : "";

  // Saved alerts that have expired (date or contract) are inspect + delete only.
  // Add drafts stay editable so a stale limit or date can still be changed before save.
  const isFullyDisabled = isPersisted ? isAlertFullyDisabled(value, now) : isExpired;

  return (
    <ManageTable.Row>
      {onSelect && (
        <div className="flex justify-center">
          <Checkbox checked={selected} onChange={onSelect} />
        </div>
      )}
      {onSelect && (
        <div
          className="border-r border-outline h-full"
          style={{ marginRight: "var(--spacing-scale-small-200, 6px)" }}
        />
      )}
      {affected && (
        <TooltipTrigger disabled={!affectedTooltip}>
          <div
            className="border rounded-md px-3 w-[130px] bg-surface-1 flex items-center box-border overflow-hidden"
            style={{ height: "27px" }}
          >
            <span data-truncate-check className="truncate block w-full">
              {affected}
            </span>
          </div>
          <Tooltip>{affectedTooltip ?? ""}</Tooltip>
        </TooltipTrigger>
      )}
      <InputOrReference
        pageId={pageId}
        value={value.limit}
        onChange={(limit) => onUpdate({ limit })}
        disabled={isFullyDisabled}
        width={limitWidth}
        tooltip={isFullyDisabled ? expirationReason : undefined}
      />
      <div className="">
        <input
          type="text"
          value={value.note ?? ""}
          disabled={isFullyDisabled}
          className="px-2 border border-outline rounded-md text-sm bg-surface-1 box-border transition-colors hover:bg-surface-2 hover:border-outline-variant focus:bg-surface-2 focus:outline-[1.5px] focus:outline-offset-[-1.5px] focus:outline-[#1D3DD5] dark:focus:outline-[#778CD9] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface-1 disabled:hover:border-outline"
          style={{ height: "27px", width: `${noteWidth}px` }}
          placeholder="Add note..."
          onChange={(e) => onUpdate({ note: e.target.value })}
        />
      </div>

      <div className="flex items-center">
        <ExpirationPicker
          expiredAt={"expired_at" in value ? value.expired_at : undefined}
          autoExpiredAt={autoExpiredAt}
          disabled={isFullyDisabled}
          onChange={(expired_at) => onUpdate({ expired_at })}
        />
      </div>

      <div className="flex items-center">
        <TooltipTrigger>
          <div>
            <StatusBadge
              status={value.status}
              isExpired={isExpired}
              isPeriodExpired={isAlertPeriodExpiredFlag || isLimitPeriodExpired}
              disabled={isFullyDisabled}
              onClick={() => onUpdate({ status: value.status === "Active" ? "Paused" : "Active" })}
            />
          </div>
          <Tooltip>
            {isFullyDisabled ? expirationReason : value.status === "Active" ? "Set to inactive" : "Set to active"}
          </Tooltip>
        </TooltipTrigger>
      </div>

      <div className="flex justify-center items-center">
        <TooltipTrigger>
          <IconButton
            disabled={isFullyDisabled}
            onClick={() => !isFullyDisabled && onUpdate({ recurring: !value.recurring })}
            className={!isFullyDisabled && value.recurring ? "!text-[var(--primary-default)]" : ""}
          >
            <RecurringAlertIcon active={isFullyDisabled ? false : !!value.recurring} />
          </IconButton>
          <Tooltip>
            {isFullyDisabled
              ? expirationReason
              : value.recurring
                ? "Turn recurring alert off"
                : "Turn recurring alert on"}
          </Tooltip>
        </TooltipTrigger>
      </div>

      <div className="flex justify-center items-center">
        <TooltipTrigger>
          <IconButton
            disabled={isFullyDisabled}
            className={!isFullyDisabled && value.sound ? "!text-[var(--primary-default)]" : ""}
            onClick={() => {
              if (isFullyDisabled) return;
              // Play the sound once per session (logging out resets this) to ensure sound can be played, the user knows what it sounds like, and set a local storage flag to avoid playing it again to avoid spamming when creating alerts.
              const playSound = !value.sound === true && localStorage.getItem("soundPlayed") !== "true";

              if (playSound) {
                playAlertSound();
                localStorage.setItem("soundPlayed", "true");
              }

              onUpdate({ sound: !value.sound });
            }}
          >
            <SoundAlertIcon active={isFullyDisabled ? false : !!value.sound} />
          </IconButton>
          <Tooltip>
            {isFullyDisabled ? expirationReason : value.sound ? "Turn alert sound off" : "Turn alert sound on"}
          </Tooltip>
        </TooltipTrigger>
      </div>

      {onRemove && (
        <div className="pl-2">
          <TooltipTrigger>
            <div>
              <Button variant="outline" color="neutral" size="iconOnly" iconWidth={36} onClick={onRemove}>
                <FaTrash />
              </Button>
            </div>
            <Tooltip>Delete this alert</Tooltip>
          </TooltipTrigger>
        </div>
      )}
    </ManageTable.Row>
  );
};
